import { useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8cmsbk13ra8mig8pj0";

const guides = [
  {
    icon: "ri-search-line",
    title: "搜索影视",
    desc: "在顶部搜索框输入片名、演员或类型关键词，即可快速定位想看的内容。",
  },
  {
    icon: "ri-filter-3-line",
    title: "分类筛选",
    desc: "进入电影或电视剧频道，按地区、年份、类型多维度筛选，并支持排序切换。",
  },
  {
    icon: "ri-play-circle-line",
    title: "查看与播放",
    desc: "点击影视卡片进入详情页，查看完整资料；点击播放入口将进入模拟播放演示界面。",
  },
  {
    icon: "ri-device-line",
    title: "多端适配",
    desc: "手机端采用顶部汉堡菜单与纵向卡片布局，电脑与移动设备均可流畅浏览。",
  },
];

const statements = [
  "91在线看是一个影视资源展示与浏览平台，致力于为喜爱电影和电视剧的用户提供便捷的搜寻与浏览体验。",
  "本站所有影视资料、封面、评分与播放数据均为前端演示数据，不代表真实版权内容。",
  "播放区域仅为前端 UI 演示效果，未接入任何真实播放服务，无法真正播放视频内容。",
  "后续如接入真实播放与内容服务，将另行公告并更新相关说明。",
];

type Status = "idle" | "submitting" | "success" | "error";

export default function About() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  usePageMeta({
    title: "关于我们 - 91在线看高清影视平台介绍",
    description:
      "了解91在线看的平台定位、使用说明、版权声明与联系方式。91在线看专注为影迷提供便捷的影视资源浏览体验。",
    keywords: "91在线看,关于我们,影视平台介绍,在线观影,版权声明",
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get("website_alt") ?? "").trim();
    if (honey) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        meta?: { message?: string; detail?: string };
        message?: string;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const message =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof message === "string" && message.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          typeof message === "string" && message && !isSpam
            ? message
            : "提交失败，请稍后重试或直接发送邮件联系我们。"
        );
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  const inputClass =
    "w-full h-11 px-4 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-500 transition-colors";

  return (
    <div className="w-full pb-16 md:pb-24">
      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8 lg:px-12">
        <div className="pt-6 md:pt-8">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        </div>
      </div>

      {/* 头部 */}
      <section className="relative mt-5 md:mt-7">
        <div className="relative h-56 md:h-72 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20an%20empty%20elegant%20movie%20theater%20with%20deep%20blue%20seats%20and%20soft%20projector%20light%2C%20moody%20atmosphere%2C%20no%20text&width=1600&height=600&seq=about-banner&orientation=landscape"
            alt="91在线看 平台介绍"
            title="91在线看 高清影视在线播放平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/40"></div>
          <div className="relative h-full mx-auto w-full max-w-[1440px] px-4 md:px-8 lg:px-12 flex flex-col justify-center">
            <span className="inline-flex w-fit items-center gap-2 px-3 py-1 rounded-full text-xs font-label bg-accent-500 text-background-50 mb-4">
              <i className="ri-information-line"></i>
              关于我们
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground-950">
              关于 91在线看
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-7">
              专业的影视资源展示平台，为热爱电影与电视剧的你，提供便捷的搜寻、筛选与浏览体验。
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8 lg:px-12">
        {/* 站点定位 */}
        <section className="pt-12 md:pt-16 grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-3">
              <span className="w-1.5 h-7 rounded-full bg-accent-500"></span>
              站点定位
            </h2>
            <div className="mt-4 space-y-4 text-sm md:text-base leading-8 text-foreground-600">
              <p>
                91在线看是一家面向广大影迷的影视视频站，主要服务热爱电影及电视剧的年轻观众与家庭用户。平台汇集最新电影、热播剧集与经典大片，强调资源丰富度与操作简易性，让每一次找片都更轻松。
              </p>
              <p>
                我们希望用户在进入播放之前，就能通过清晰的分类、直观的筛选和丰富的内容卡片，快速找到想看的作品，获得沉浸式的观影选择体验。
              </p>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: "ri-stack-line", label: "丰富资源", value: "电影 + 剧集" },
                { icon: "ri-timer-flash-line", label: "更新频率", value: "每日更新" },
                { icon: "ri-smartphone-line", label: "观看终端", value: "电脑 / 手机" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg bg-background-100 border border-background-200/70 px-5 py-4 flex items-center gap-3"
                >
                  <span className="w-10 h-10 rounded-md bg-accent-100 text-accent-900 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    <p className="font-heading text-base font-bold text-foreground-950">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <aside className="rounded-lg bg-primary-950 p-6 md:p-7">
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">联系我们</h3>
            <p className="text-sm leading-7 text-foreground-600">
              如对内容、使用体验有任何建议，或涉及版权相关事宜，欢迎通过以下方式与我们取得联系。
            </p>
            <ul className="mt-5 space-y-3 text-sm">
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="w-9 h-9 rounded-md bg-primary-800 flex items-center justify-center text-accent-400 shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                feedback@91-zaixiankan.demo
              </li>
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="w-9 h-9 rounded-md bg-primary-800 flex items-center justify-center text-accent-400 shrink-0">
                  <i className="ri-time-line text-lg"></i>
                </span>
                响应时间：工作日 24 小时内
              </li>
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="w-9 h-9 rounded-md bg-primary-800 flex items-center justify-center text-accent-400 shrink-0">
                  <i className="ri-shield-check-line text-lg"></i>
                </span>
                所有反馈仅用于内容改进
              </li>
            </ul>
            <p className="mt-5 text-xs text-foreground-500">
              注：本站为演示性质站点，联系方式与响应时间为示例说明。
            </p>
          </aside>
        </section>

        {/* 使用说明 */}
        <section className="pt-12 md:pt-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-3">
            <span className="w-1.5 h-7 rounded-full bg-accent-500"></span>
            使用说明
          </h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {guides.map((g, i) => (
              <div
                key={g.title}
                className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6"
              >
                <div className="flex items-center justify-between">
                  <span className="w-11 h-11 rounded-md bg-secondary-900/60 text-secondary-200 flex items-center justify-center">
                    <i className={`${g.icon} text-2xl`}></i>
                  </span>
                  <span className="font-heading text-2xl font-extrabold text-background-300">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="mt-4 font-heading text-base md:text-lg font-bold text-foreground-950">
                  {g.title}
                </h3>
                <p className="mt-2 text-sm leading-7 text-foreground-600">{g.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 版权声明 */}
        <section className="pt-12 md:pt-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-3">
            <span className="w-1.5 h-7 rounded-full bg-accent-500"></span>
            版权与免责声明
          </h2>
          <ul className="mt-4 space-y-3">
            {statements.map((s) => (
              <li
                key={s}
                className="flex items-start gap-3 rounded-lg bg-background-100 border border-background-200/70 px-5 py-4"
              >
                <i className="ri-checkbox-circle-line text-accent-500 text-lg mt-0.5 shrink-0"></i>
                <span className="text-sm leading-7 text-foreground-600">{s}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* 意见反馈表单 */}
        <section className="pt-12 md:pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-3">
                <span className="w-1.5 h-7 rounded-full bg-accent-500"></span>
                意见反馈
              </h2>
              <p className="mt-4 text-sm md:text-base leading-8 text-foreground-600">
                你的每一条建议，都会帮助我们做得更好。填写下方表单告诉我们你的想法；
                也可以邮件联系 <span className="text-accent-400">feedback@91-zaixiankan.demo</span>。
              </p>
              <p className="mt-4 text-xs text-foreground-500">
                提示：本表单为可正常提交的反馈入口，提交后我们会在后台收到你的留言。
              </p>
            </div>

            <div className="lg:col-span-3">
              <form
                data-readdy-form
                id="about-feedback-form"
                onSubmit={handleSubmit}
                className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-7"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="fb-name" className="block text-sm text-foreground-700 mb-2">
                      姓名 / 昵称
                    </label>
                    <input
                      id="fb-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入你的称呼"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="fb-email" className="block text-sm text-foreground-700 mb-2">
                      邮箱
                    </label>
                    <input
                      id="fb-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于回复你，请填写正确邮箱"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="fb-type" className="block text-sm text-foreground-700 mb-2">
                    反馈类型
                  </label>
                  <select id="fb-type" name="type" required defaultValue="" className={inputClass}>
                    <option value="" disabled>
                      请选择反馈类型
                    </option>
                    <option value="内容建议">内容建议</option>
                    <option value="功能问题">功能问题</option>
                    <option value="播放体验">播放体验</option>
                    <option value="版权相关">版权相关</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div className="mt-4">
                  <label htmlFor="fb-message" className="block text-sm text-foreground-700 mb-2">
                    反馈内容
                  </label>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你的建议或问题（最多 500 字）"
                    className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-500 transition-colors resize-none"
                  ></textarea>
                </div>

                {/* 反垃圾字段 */}
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-honeypot-field"
                />

                {status === "success" && (
                  <div className="mt-5 flex items-center gap-2 rounded-md bg-secondary-100/80 text-secondary-900 px-4 py-3 text-sm">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                    提交成功，感谢你的反馈！我们会认真阅读每一条建议。
                  </div>
                )}
                {status === "error" && (
                  <div className="mt-5 flex items-center gap-2 rounded-md bg-accent-100/80 text-accent-900 px-4 py-3 text-sm">
                    <i className="ri-error-warning-line text-lg"></i>
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-5 h-11 w-full sm:w-auto px-8 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm md:text-base font-label whitespace-nowrap transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? "提交中…" : "提交反馈"}
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}